import {
  LdtOrderDetailFieldsFragment,
  NccnRiskCategory,
  SpecimenType,
  TStage,
  UploadedFileCategory,
} from 'src/types';
import { WorkflowType } from 'src/types/workflows';

export const CLINICAL_VARIABLES_MISSING_MESSAGE =
  'Clinical variables are missing';

export const ORDERING_PROVIDER_MISSING_MESSAGE = 'Ordering Provider is missing';

export const PATHOLOGY_LAB_MISSING_MESSAGE = 'Pathology Lab is missing';

export const PATHOLOGY_REPORT_MISSING_MESSAGE =
  'Please upload the Pathology Report.';

export const FACE_SHEET_MISSING_MESSAGE =
  'Please upload at least 1 Demographic / Face sheet document.';

/** Soft clinical / Not Provided apply only to Clinic and Direct Upload. */
const SOFT_CLINICAL_SUBMIT_WORKFLOWS: ReadonlySet<WorkflowType> = new Set([
  WorkflowType.CLINIC,
  WorkflowType.DIRECT_UPLOAD_LAB,
]);

export function isSoftClinicalSubmitWorkflow(
  workflowType: WorkflowType,
): boolean {
  return SOFT_CLINICAL_SUBMIT_WORKFLOWS.has(workflowType);
}

/**
 * CS/Sales soft submit: unset clinicals/OP allowed only on Clinic + Direct
 * Upload when not editing a QC Pending order.
 */
export function allowSoftClinicalSubmit(
  isCSOrSalesUser: boolean,
  isEditingQCPendingOrder: boolean,
  workflowType: WorkflowType,
): boolean {
  return (
    isCSOrSalesUser &&
    !isEditingQCPendingOrder &&
    isSoftClinicalSubmitWorkflow(workflowType)
  );
}

export function isPsaUnset(psa: number | null | undefined): boolean {
  return psa == null;
}

export function isOrderingProviderMissing(
  order: Pick<LdtOrderDetailFieldsFragment, 'orderingProvider'>,
): boolean {
  return !order.orderingProvider?.id;
}

function isTStageUnset(tStage: TStage | null | undefined): boolean {
  return tStage == null || tStage === TStage.NotProvided;
}

function isNccnUnset(
  nccnRiskCategory: NccnRiskCategory | null | undefined,
): boolean {
  return nccnRiskCategory === NccnRiskCategory.NotProvided;
}

/**
 * True when any clinical variable that CS/Sales may leave unset is still
 * missing for QC Approval (null PSA, Not Provided / null T Stage, and for
 * biopsy Not Provided NCCN).
 */
export function isClinicalVariablesMissing(
  order: Pick<
    LdtOrderDetailFieldsFragment,
    'psa' | 'tStage' | 'nccnRiskCategory' | 'specimenType'
  >,
): boolean {
  if (isPsaUnset(order.psa) || isTStageUnset(order.tStage)) {
    return true;
  }

  if (
    order.specimenType === SpecimenType.ProstateBiopsy &&
    isNccnUnset(order.nccnRiskCategory)
  ) {
    return true;
  }

  return false;
}

const PATHOLOGY_LAB_REQUIRED_FIELDS = [
  ['name', 'Name'],
  ['phoneNumber', 'Phone Number'],
  ['faxNumber', 'Fax Number'],
  ['street', 'Street'],
  ['city', 'City'],
  ['state', 'State'],
  ['zipCode', 'Zip Code'],
  ['country', 'Country'],
] as const;

/**
 * Pathology Lab missing entirely, or Salesforce field gaps for an existing lab.
 */
export function getPathologyLabMissingInfoMessages(
  order: Pick<LdtOrderDetailFieldsFragment, 'pathologyLab'>,
): string[] {
  if (!order.pathologyLab) {
    return [PATHOLOGY_LAB_MISSING_MESSAGE];
  }

  const missing = PATHOLOGY_LAB_REQUIRED_FIELDS.filter(
    ([key]) => !order.pathologyLab?.[key],
  ).map(([, label]) => label);

  if (missing.length === 0) {
    return [];
  }

  return [
    `Please add the missing Pathology Lab fields in Salesforce - ${missing.join(', ')}.`,
  ];
}

function hasUploadedFileCategory(
  order: Pick<LdtOrderDetailFieldsFragment, 'uploadedFiles'>,
  category: UploadedFileCategory,
): boolean {
  return !!order.uploadedFiles?.edges.some(
    (e) => e?.node?.category === category,
  );
}

/** Clinic QC required uploads: Pathology Report and Face Sheet. */
export function getClinicRequiredUploadMissingInfoMessages(
  order: Pick<LdtOrderDetailFieldsFragment, 'uploadedFiles'>,
): string[] {
  const messages: string[] = [];

  if (!hasUploadedFileCategory(order, UploadedFileCategory.PathologyReport)) {
    messages.push(PATHOLOGY_REPORT_MISSING_MESSAGE);
  }

  if (!hasUploadedFileCategory(order, UploadedFileCategory.FaceSheet)) {
    messages.push(FACE_SHEET_MISSING_MESSAGE);
  }

  return messages;
}
